import * as React from 'react';
import { NewbuildingCarousel, TDirection, TOnSlideChangeParams } from '@cian/frontend-newbuilding-carousel-component';
import { Image } from '@cian/ui-kit/image';
import { isBackgroundVideoSupported } from '../../../browser/utils';
import { IMediaItem } from '../../types/newbuilding/media';
import * as style from './Carousel.css';
import { ArrowControls } from './parts/ArrowControls';
import { BackgroundVideo } from './parts/BackgroundVideo';

import { NoImage } from './parts/NoImage';
import { firstPaintOptions, mediaSettings } from './utils/carouselSettings';

export interface ICarouselProps {
  JKid: number;
  media: IMediaItem[] | null;
  backgroundVideo: string | null;
  basic: React.ReactChild;
}

export interface ICarouselState {
  slides: React.ReactNode[] | null;
  currentSlide: number;
}

export class Carousel extends React.Component<ICarouselProps, ICarouselState> {
  public constructor(props: ICarouselProps) {
    super(props);

    const { media } = this.props;

    this.state = {
      slides: media && this.prepareSlides(media.slice(0, 10), this.props.backgroundVideo),
      currentSlide: 1,
    };
  }

  public componentDidMount() {
    const { media, backgroundVideo } = this.props;

    this.setState({
      slides: media && this.prepareSlides(media, backgroundVideo),
    });
  }

  public prepareSlides = (mediaData: IMediaItem[], backgroundVideo?: string | null): React.ReactNode[] => {
    const shouldAddVideo = !!backgroundVideo && isBackgroundVideoSupported();

    const images = mediaData.map(media => (
      <Image key={media.fullUrl} src={media.fullUrl} alt={media.alt || ''} objectFit={'cover'} />
    ));

    return [
      shouldAddVideo && (
        <BackgroundVideo poster={mediaData[0] && mediaData[0].fullUrl} url={backgroundVideo as string} />
      ),
      ...images,
    ].filter(Boolean);
  };

  public render() {
    const { slides, currentSlide } = this.state;

    if (!slides || !slides.length) {
      return (
        <div className={style['carousel']}>
          <NoImage />
        </div>
      );
    }

    return (
      <div className={style['carousel']}>
        <NewbuildingCarousel
          media={mediaSettings}
          firstPaintOptions={firstPaintOptions}
          slides={slides}
          defaultCurrentSlide={currentSlide}
          onSlideChange={this.onSlideChange}
        />
        {this.props.basic}
        {slides.length > 1 && (
          <ArrowControls
            disableLeft={currentSlide === 1}
            disableRight={currentSlide === slides.length}
            clickHandler={this.changeSlide}
          />
        )}
      </div>
    );
  }

  private onSlideChange = ({ currentSlide }: TOnSlideChangeParams) => {
    this.setState({
      currentSlide,
    });
  };

  private changeSlide = (direction: TDirection) => {
    this.setState({
      currentSlide: this.state.currentSlide + direction * 1,
    });
  };
}
